import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminApiService } from '../services/api'
import { AdminUser, SubscriptionTier } from '../types/admin'
import CassetteLoader from '../components/CassetteLoader'
import { 
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  UserIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export function Users() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | ''>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [error, setError] = useState<string | null>(null)
  type UserSortField = 'firstSeenAt' | 'updatedAt'
  type UserSortDir = 'asc' | 'desc'
  const [sortBy, setSortBy] = useState<UserSortField>('firstSeenAt')
  const [sortDir, setSortDir] = useState<UserSortDir>('desc')

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      // Reset to page 1 when search changes
      setCurrentPage(1)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    loadUsers()
  }, [currentPage, debouncedSearchTerm, selectedTier, sortBy, sortDir])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      const filters: Record<string, string> = {
        page: String(currentPage),
        limit: '50',
        sort_by: sortBy,
        sort_dir: sortDir,
      }

      if (debouncedSearchTerm) filters.search = debouncedSearchTerm
      if (selectedTier) filters.subscription_tier = selectedTier

      const data = await AdminApiService.getUsers(filters)
      
      // Handle response safely with null checks
      if (data && data.users) {
        setUsers(data.users)
        setTotalPages(data.totalPages || 1)
        setTotalUsers(data.total || 0)
      } else {
        console.error('Unexpected API response format:', data)
        setUsers([])
        setTotalPages(1)
        setTotalUsers(0)
      }
    } catch (err: any) {
      console.error('Error loading users:', err)
      setError(err.message || 'Failed to load users')
      
      // Reset state on error
      setUsers([])
      setTotalPages(1)
      setTotalUsers(0)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      const filters: Record<string, string> = {
        sort_by: sortBy,
        sort_dir: sortDir,
      }
      if (debouncedSearchTerm) filters.search = debouncedSearchTerm
      if (selectedTier) filters.subscription_tier = selectedTier

      await AdminApiService.exportUsers(filters)
      toast.success('Users exported successfully')
    } catch (err: any) {
      toast.error('Failed to export users')
    }
  }

  const getTierBadge = (tier: SubscriptionTier | undefined) => {
    const safeTier = tier || SubscriptionTier.STARTER
    const styles = {
      [SubscriptionTier.STARTER]: 'bg-gray-100 text-gray-800',
      [SubscriptionTier.ARTIST]: 'bg-blue-100 text-blue-800',
      [SubscriptionTier.STUDIO]: 'bg-green-100 text-green-800'
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[safeTier] || styles[SubscriptionTier.STARTER]}`}>
        {safeTier.charAt(0).toUpperCase() + safeTier.slice(1)}
      </span>
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') {
      return '—'
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const toggleSort = (field: UserSortField) => {
    if (sortBy === field) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortDir('desc')
    }
    setCurrentPage(1)
  }

  const sortIcon = (field: UserSortField) => {
    if (sortBy !== field) {
      return <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
    }
    return sortDir === 'asc'
      ? <ChevronUpIcon className="h-4 w-4 text-primary-600" />
      : <ChevronDownIcon className="h-4 w-4 text-primary-600" />
  }

  const navigateToUser = (userId: string) => {
    navigate(`/users/${userId}`)
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <CassetteLoader size={80} text="Loading users..." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">Manage users and view their details</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 sm:max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email, UID, name, or UTM parameters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Tier Filter */}
            <div className="sm:w-48">
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value as SubscriptionTier | '')}
                className="input"
              >
                <option value="">All Tiers</option>
                <option value={SubscriptionTier.STARTER}>Starter</option>
                <option value={SubscriptionTier.ARTIST}>Artist</option>
                <option value={SubscriptionTier.STUDIO}>Studio</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="btn btn-primary btn-sm w-full sm:w-auto"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Total Users</div>
          <div className="text-2xl font-bold text-gray-900">{totalUsers.toLocaleString()}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Subscribed Users</div>
          <div className="text-2xl font-bold text-green-600">
            {users.filter(u => u.subscriptionTier && u.subscriptionTier !== SubscriptionTier.STARTER).length}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Current Page</div>
          <div className="text-2xl font-bold text-gray-900">
            {currentPage} of {totalPages}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="overflow-hidden">
          <div className="overflow-x-auto min-w-full">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="min-w-[120px]">User</th>
                  <th className="min-w-[120px]">Tier</th>
                  <th className="min-w-[140px]">
                    <button
                      type="button"
                      onClick={() => toggleSort('firstSeenAt')}
                      className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
                    >
                      Created
                      {sortIcon('firstSeenAt')}
                    </button>
                  </th>
                  <th className="min-w-[140px]">
                    <button
                      type="button"
                      onClick={() => toggleSort('updatedAt')}
                      className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
                    >
                      Last Updated
                      {sortIcon('updatedAt')}
                    </button>
                  </th>
                  <th className="min-w-[120px]">UTM Source</th>
                  <th className="min-w-[120px]">UTM Medium</th>
                  <th className="min-w-[120px]">UTM Campaign</th>
                  <th className="min-w-[120px]">UTM Term</th>
                  <th className="min-w-[120px]">UTM Content</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="min-w-[120px]">
                      <button
                        onClick={() => navigateToUser(user.id)}
                        className="flex items-center gap-2 text-left hover:text-primary-600"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <UserIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.displayName || user.email}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </button>
                    </td>
                    <td className="min-w-[120px]">
                      {getTierBadge(user.subscriptionTier)}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[140px] whitespace-nowrap">
                      {formatDate(user.createdAt || user.firstSeenAt)}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[140px] whitespace-nowrap">
                      {formatDate(user.lastUpdatedAt || user.createdAt || user.firstSeenAt)}
                    </td>
                    <td className="min-w-[120px]">
                      {user.utmSource ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.utmSource}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="min-w-[120px]">
                      {user.utmMedium ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {user.utmMedium}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="min-w-[120px]">
                      {user.utmCampaign ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {user.utmCampaign}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="min-w-[120px]">
                      {user.utmTerm ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {user.utmTerm}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="min-w-[120px]">
                      {user.utmContent ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {user.utmContent}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-ghost btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-ghost btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span> ({totalUsers} total users)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
